import { useState } from 'react'
import { useEffect } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { ManagementFormLayout } from 'components/ManagementFormLayout'
import { routes } from 'constants/routes'
import { accountManagementStrings, commonStrings } from 'constants/strings'
import { useAdminOrganizations, useCreateAdminAccount } from 'hooks'
import { API_ERROR_CODE, getApiErrorAccountId, getApiErrorCode } from 'utils/apiErrorCode'
import { EOrganizationMemberRole } from 'types'

export default function AccountManagementCreate() {
  const navigate = useNavigate()
  const strings = accountManagementStrings.create

  const [name, setName] = useState('')
  const [organizationId, setOrganizationId] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<EOrganizationMemberRole>(
    EOrganizationMemberRole.STANDARD,
  )
  const [validationError, setValidationError] = useState<string | undefined>()
  const [emailExistsError, setEmailExistsError] = useState(false)
  const [existingAccountId, setExistingAccountId] = useState<number | undefined>()

  const {
    organizations,
    loading: isOrganizationsLoading,
    error: organizationsError,
  } = useAdminOrganizations()

  useEffect(() => {
    if (!organizationId && organizations.length > 0) {
      setOrganizationId(String(organizations[0].id))
    }
  }, [organizationId, organizations])

  const { create, isSubmitting, error: apiError } = useCreateAdminAccount({
    onSuccess: () =>
      navigate(routes.ACCOUNT_MANAGEMENT, {
        replace: true,
        state: {
          snackbarMessage: accountManagementStrings.messages.createSuccess,
        },
      }),
    onError: (caught: unknown) => {
      const isEmailExists =
        getApiErrorCode(caught) === API_ERROR_CODE.ACCOUNT_EMAIL_EXISTS
      setEmailExistsError(isEmailExists)
      setExistingAccountId(isEmailExists ? getApiErrorAccountId(caught) : undefined)
    },
  })

  const error =
    validationError ||
    (emailExistsError ? strings.errors.emailExists : undefined) ||
    apiError ||
    organizationsError
  const isSubmitDisabled =
    isSubmitting || isOrganizationsLoading || organizations.length === 0

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitDisabled) return

    setValidationError(undefined)
    setEmailExistsError(false)
    setExistingAccountId(undefined)

    if (organizations.length === 0) {
      setValidationError(strings.errors.noOrganizations)
      return
    }

    const trimmedName = name.trim()
    const trimmedEmail = email.trim()

    if (!organizationId) {
      setValidationError(strings.errors.organizationRequired)
      return
    }

    if (!trimmedName || !trimmedEmail || !password) {
      setValidationError(strings.errors.required)
      return
    }

    if (password.length < 6) {
      setValidationError(strings.errors.passwordTooShort)
      return
    }

    create({
      organizationId: Number(organizationId),
      name: trimmedName,
      email: trimmedEmail,
      password,
      role,
    })
  }

  return (
    <ManagementFormLayout
      kicker={strings.kicker}
      title={strings.title}
      description={strings.description}
      backTo={routes.ACCOUNT_MANAGEMENT}
    >
      <form
        className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-1">
          <h2 className="text-sm font-semibold text-ink">
            {strings.form.title}
          </h2>
          <p className="text-xs text-muted">{strings.form.description}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.organization.label}
            <select
              name="organizationId"
              className="h-11 w-full rounded-2xl border border-border/80 bg-white/80 px-3 text-sm text-ink shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
              disabled={isSubmitDisabled}
              value={organizationId}
              onChange={(event) => setOrganizationId(event.target.value)}
            >
              {isOrganizationsLoading && (
                <option value="">{commonStrings.loading}</option>
              )}
              {!isOrganizationsLoading && organizations.length === 0 && (
                <option value="">{strings.errors.noOrganizations}</option>
              )}
              {!isOrganizationsLoading &&
                organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>
                    {organization.id}: {organization.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.name.label}
            <Input
              name="name"
              placeholder={strings.form.fields.name.placeholder}
              autoComplete="name"
              required
              disabled={isSubmitting}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.email.label}
            <Input
              name="email"
              type="email"
              placeholder={strings.form.fields.email.placeholder}
              autoComplete="email"
              required
              disabled={isSubmitting}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.password.label}
            <Input
              name="password"
              type="password"
              placeholder={strings.form.fields.password.placeholder}
              autoComplete="new-password"
              required
              minLength={6}
              disabled={isSubmitting}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.role.label}
            <select
              name="role"
              className="h-11 w-full rounded-2xl border border-border/80 bg-white/80 px-3 text-sm text-ink shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
              disabled={isSubmitting}
              value={role}
              onChange={(event) =>
                setRole(event.target.value as EOrganizationMemberRole)
              }
            >
              <option value={EOrganizationMemberRole.STANDARD}>
                {strings.form.roleOptions.standard}
              </option>
            </select>
          </label>
        </div>
        {error && <Alert>{error}</Alert>}
        <div className="flex flex-wrap justify-end gap-3">
          {emailExistsError && existingAccountId !== undefined && (
            <Button
              type="button"
              variant="outline"
              size="md"
              disabled={isSubmitting}
              onClick={() =>
                navigate(
                  routes.ACCOUNT_MANAGEMENT_EDIT.replace(
                    ':accountId',
                    String(existingAccountId),
                  ),
                )
              }
            >
              {strings.errors.openExistingAccount}
            </Button>
          )}
          <Button size="md" type="submit" disabled={isSubmitDisabled}>
            {isSubmitting
              ? `${strings.actions.submit}${commonStrings.loadingEllipsis}`
              : strings.actions.submit}
          </Button>
        </div>
      </form>
    </ManagementFormLayout>
  )
}
